import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import { type TimeRange } from '~/swr-hooks/clips/transcript/types';

type ChangedTranscriptSegmentRange = Pick<TimeRange, 'startTimeMs' | 'endTimeMs'>;

type CachedChunk = {
  startTimeMs: number;
  endTimeMs: number;
  data: { startTimeMs: number; endTimeMs: number }[];
};

const rangesOverlapOrTouch = (first: TimeRange, second: TimeRange) =>
  first.startTimeMs <= second.endTimeMs && second.startTimeMs <= first.endTimeMs;

/**
 * Returns true if we have fetched data covering the gap and confirmed there are no segments in it.
 * Returns false if the gap is not cached (we can't know) or if segments exist in it.
 */
const gapIsKnownEmpty = (cachedData: CachedChunk[], gapStart: number, gapEnd: number) => {
  const chunkCoveringGap = cachedData.find((chunk) => chunk.startTimeMs <= gapStart && chunk.endTimeMs >= gapEnd);

  if (!chunkCoveringGap) {
    return false;
  }

  return !chunkCoveringGap.data.some((s) => s.startTimeMs >= gapStart && s.endTimeMs <= gapEnd);
};

const splitRangeByMaxLength = (range: TimeRange, maxLengthMs: number): TimeRange[] => {
  if (range.endTimeMs - range.startTimeMs <= maxLengthMs) {
    return [range];
  }

  const chunks: TimeRange[] = [];
  let currentStart = range.startTimeMs;

  while (currentStart < range.endTimeMs) {
    const currentEnd = Math.min(currentStart + maxLengthMs, range.endTimeMs);
    chunks.push({ startTimeMs: currentStart, endTimeMs: currentEnd });
    currentStart = currentEnd;
  }

  return chunks;
};

export const getRefetchRangesForChangedSegments = ({
  changedSegments,
  cachedData = [],
  maxRangeMs = MAX_TRANSCRIPT_RANGE_IN_MS,
}: {
  changedSegments: ChangedTranscriptSegmentRange[];
  cachedData?: CachedChunk[];
  maxRangeMs?: number;
}): TimeRange[] => {
  if (changedSegments.length === 0) {
    return [];
  }

  const sortedRanges = [...changedSegments].sort((left, right) => left.startTimeMs - right.startTimeMs);
  const mergedRanges = sortedRanges.reduce<TimeRange[]>((acc, range) => {
    const previous = acc[acc.length - 1];

    if (!previous) {
      acc.push({ ...range });
      return acc;
    }

    const shouldMerge =
      rangesOverlapOrTouch(previous, range) || gapIsKnownEmpty(cachedData, previous.endTimeMs, range.startTimeMs);

    if (shouldMerge) {
      previous.endTimeMs = Math.max(previous.endTimeMs, range.endTimeMs);
      return acc;
    }

    acc.push({ ...range });
    return acc;
  }, []);

  return mergedRanges.flatMap((range) => splitRangeByMaxLength(range, maxRangeMs));
};
